import { BorderTable } from '@/components/BorderTable';
import { LoadingSkeletonList } from '@/components/SkeletonLoading';
import {
  SKELETON_GAP_XS,
  SKELETON_HEIGHT_XS,
} from '@/components/SkeletonLoading/constants';
import { useGetTransactionItems } from '@/context/services/transaction_items/List/TransactionItems.service';
import { DetailDrawer } from '@/features/transaction_items/components/DetailDrawer/DetailDrawer';
import { useDeleteConfirm } from '@/features/transaction_items/components/Drawer/hooks/useDeleteConfirm';
import { TransactionItemDrawer } from '@/features/transaction_items/components/Drawer/TransactionItemDrawer';
import {
  TransactionItemGeneralForm,
  Props as TransactionItemGeneralFormProps,
} from '@/features/transaction_items/components/Form/GeneralForm/TransactionItemGeneralForm';
import { useSubmitUpdateReorderTransactionItem } from '@/features/transaction_items/components/Form/hooks/useSubmitUpdateReorderTransactionItem';
import { ReorderableTable } from '@/features/transaction_items/List/ReorderableTable/ReorderableTable';
import { useTransactionItemsColumns } from '@/features/transaction_items/List/Table/Column/column';
import { useSubmitDeleteTransactionItem } from '@/features/transaction_items/List/Table/hooks/useDeleteTransactionItem';
import {
  MAX_CUSTOM_TRANSACTION_ITEMS_COUNT,
  TableAction,
} from '@/features/transaction_items/List/Table/TableAction/TableAction';
import {
  TransactionItemDrawerMode,
  TransactionItemDrawerModeEnum,
  TransactionItemsColumn,
} from '@/features/transaction_items/List/type';
import {
  ModernPaginationProps,
  TableProps,
  useToastAPI,
} from '@moneyforward/ap-frontend-components';
import {
  FC,
  memo,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useParams } from 'react-router-dom';

export type Props = {
  table: TransactionItemsTableProps;
  onDetailClick: (id: string) => void;
  customTransactionItemsCount?: number;
};

export type TransactionItemsViewTableProps = {
  tableBodyHeight: number;
  isReorderingMode: boolean;
  enterReorderingMode: () => void;
  exitReorderingMode: () => void;
  showFullScreenLoading: (loading: boolean) => void;
  isFullScreenLoading?: boolean;
  onOrderChange: (isOrderChanged: boolean) => void;
  isOrderChanged: boolean;
  confirmCancelReordering: () => Promise<boolean>;
};

export type TransactionItemsTableProps = Omit<
  TableProps<TransactionItemsColumn>,
  'columns' | 'emptyType' | 'pagination'
>;

export const TransactionItemsTable: FC<Props> = memo(
  ({ table, onDetailClick, customTransactionItemsCount }) => {
    const columns = useTransactionItemsColumns({
      onDetailClick,
      customTransactionItemsCount,
    });
    const pagination: ModernPaginationProps = useMemo(() => {
      return {
        currentPage: 1,
        maxPage: 1,
        showPerPage: true,
      };
    }, []);
    return (
      <BorderTable
        {...table}
        columns={columns}
        legacy={false}
        pagination={pagination}
        disableRowStyling
      />
    );
  }
);
TransactionItemsTable.displayName = 'TransactionItemsTable';

export const InnerTable: FC<TransactionItemsViewTableProps> = memo(
  ({
    tableBodyHeight,
    isReorderingMode,
    enterReorderingMode,
    exitReorderingMode,
    showFullScreenLoading,
    isFullScreenLoading,
    onOrderChange,
    isOrderChanged,
    confirmCancelReordering,
  }) => {
    const { report_type_id } = useParams();
    const { data, isLoadingGetTransactionItems, refetch } =
      useGetTransactionItems({
        report_type_id: report_type_id ?? '',
      });
    const notify = useToastAPI();
    const sortPriority = useCallback(
      (data: TransactionItemsColumn[]) =>
        [...data].sort((a, b) => a.priority - b.priority),
      []
    );
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [mode, setMode] = useState<TransactionItemDrawerMode>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedDetailId, setSelectedDetailId] = useState<
      string | undefined
    >(undefined);
    const [isDetailLoading, setIsDetailLoading] = useState(false);
    const [isShowUpdateConfirm, setIsShowUpdateConfirm] = useState(false);
    // reordered list
    const [dataSource, setDataSource] = useState<TransactionItemsColumn[]>(() =>
      sortPriority(data)
    );

    const tbodyStyle = useMemo(() => {
      return {
        height: tableBodyHeight,
      };
    }, [tableBodyHeight]);

    const customTransactionItemsCount = useMemo(() => {
      return data.filter((item) => item.customizable).length;
    }, [data]);

    const openDrawer = useCallback(() => {
      setIsDrawerOpen(true);
    }, []);

    const closeDrawer = useCallback(() => {
      setIsDrawerOpen(false);
    }, []);
    const refreshDataSource = useCallback(async () => {
      const data = await refetch();
      setDataSource(sortPriority(data));
    }, [refetch, sortPriority]);
    const resetDataSource = useCallback(() => {
      setDataSource(sortPriority(data));
    }, [data, sortPriority]);
    const handlerSuccess = useCallback(
      async (successMsg: string) => {
        await refreshDataSource();
        notify.success({
          title: successMsg,
          duration: 5,
        });
      },
      [notify, refreshDataSource]
    );
    const handlerError = useCallback(
      (errorMsg: string) => {
        notify.error({
          title: errorMsg,
          duration: 5,
        });
      },
      [notify]
    );

    const { onSubmitReorder } = useSubmitUpdateReorderTransactionItem({
      handlerSuccess,
      handlerError,
      showLoading: showFullScreenLoading,
    });

    const onCancelOrdering = useCallback(async () => {
      if (isOrderChanged) {
        const isConfirmed = await confirmCancelReordering();
        if (isConfirmed) {
          exitReorderingMode();
          resetDataSource();
        }
      } else {
        exitReorderingMode();
      }
    }, [
      isOrderChanged,
      resetDataSource,
      confirmCancelReordering,
      exitReorderingMode,
    ]);

    const table: TransactionItemsTableProps = useMemo(() => {
      const sortedData = sortPriority(data);
      return {
        data: sortedData,
        scroll: sortedData.length > 0 ? { y: tableBodyHeight } : undefined,
        components:
          sortedData.length === 0
            ? {
                body: {
                  wrapper: ({ children }: { children: React.ReactNode }) => (
                    <tbody style={tbodyStyle}>{children}</tbody>
                  ),
                },
              }
            : undefined,
        tableAction: (
          <TableAction
            customTransactionItemsCount={customTransactionItemsCount}
            onCreate={() => {
              openDrawer();
              setMode(TransactionItemDrawerModeEnum.CREATE);
            }}
            isDisabled={
              customTransactionItemsCount >= MAX_CUSTOM_TRANSACTION_ITEMS_COUNT
            }
            onReordering={enterReorderingMode}
            isReorderingMode={isReorderingMode}
            onCancelOrdering={() => {
              onCancelOrdering();
            }}
            onSubmitReorder={() => {
              onSubmitReorder(dataSource.map((item) => item.id));
              exitReorderingMode();
            }}
            isSubmitDisabled={!isOrderChanged}
          />
        ),
      };
    }, [
      sortPriority,
      data,
      tableBodyHeight,
      customTransactionItemsCount,
      enterReorderingMode,
      isReorderingMode,
      isOrderChanged,
      tbodyStyle,
      openDrawer,
      onCancelOrdering,
      onSubmitReorder,
      dataSource,
      exitReorderingMode,
    ]);

    const showLoading = useCallback((loading: boolean) => {
      setIsLoading(loading);
    }, []);

    useEffect(() => {
      if (onOrderChange) {
        const sortedData = sortPriority(data);
        const isOrderChangedValue =
          dataSource.length > 0 &&
          dataSource.some((item, index) => item.id !== sortedData[index]?.id);
        onOrderChange(isOrderChangedValue);
      }
    }, [data, dataSource, onOrderChange, sortPriority]);

    const onDetailClick = useCallback(
      (id: string) => {
        setSelectedDetailId(id);
        openDrawer();
        setMode(TransactionItemDrawerModeEnum.DETAIL);
      },
      [openDrawer, setMode, setSelectedDetailId]
    );

    const onSetMode = useCallback(
      (mode: TransactionItemDrawerMode) => {
        setMode(mode);
      },
      [setMode]
    );

    const onShowUpdateConfirm = useCallback((show: boolean) => {
      setIsShowUpdateConfirm(show);
    }, []);

    const generalFormProps: TransactionItemGeneralFormProps = useMemo(() => {
      return {
        mode: mode,
        showLoading: showLoading,
        isLoading: isLoading,
        closeDrawer: closeDrawer,
        onShowUpdateConfirm: onShowUpdateConfirm,
        onSetMode: onSetMode,
        handleSuccess: refreshDataSource,
      };
    }, [
      mode,
      showLoading,
      isLoading,
      closeDrawer,
      onShowUpdateConfirm,
      onSetMode,
      refreshDataSource,
    ]);

    const { confirm, ConfirmModal } = useDeleteConfirm();
    const { onDelete } = useSubmitDeleteTransactionItem({
      transaction_item_id: selectedDetailId ?? '',
      handlerSuccess: () => {
        closeDrawer();
      },
      showLoading: showLoading,
    });

    const onDeleteClick = useCallback(async () => {
      const isConfirmed = await confirm();
      if (isConfirmed) {
        onDelete();
      }
    }, [confirm, onDelete]);

    return (
      <>
        {isReorderingMode ? (
          <>
            <ReorderableTable
              table={table}
              dataSource={dataSource}
              setDataSource={setDataSource}
            />
          </>
        ) : (
          <>
            {isFullScreenLoading || isLoadingGetTransactionItems ? (
              // Hide layout shift
              <FallbackTable
                tableBodyHeight={tableBodyHeight}
                isReorderingMode={isReorderingMode}
                enterReorderingMode={enterReorderingMode}
                exitReorderingMode={exitReorderingMode}
                showFullScreenLoading={showFullScreenLoading}
                emptyType='loading'
                onOrderChange={() => {}}
                isOrderChanged={false}
                confirmCancelReordering={() => Promise.resolve(false)}
              />
            ) : (
              <TransactionItemsTable
                table={table}
                onDetailClick={onDetailClick}
                customTransactionItemsCount={customTransactionItemsCount}
              />
            )}
            <TransactionItemDrawer
              visible={isDrawerOpen}
              onClose={closeDrawer}
              mode={mode}
              isLoading={isLoading}
              onSetMode={onSetMode}
              isDetailLoading={isDetailLoading}
              isShowUpdateConfirm={isShowUpdateConfirm}
              onDeleteClick={onDeleteClick}
            >
              {mode === TransactionItemDrawerModeEnum.CREATE ? (
                <TransactionItemGeneralForm {...generalFormProps} />
              ) : (
                selectedDetailId && (
                  <DetailDrawer
                    {...generalFormProps}
                    selectedDetailId={selectedDetailId}
                    setIsDetailLoading={setIsDetailLoading}
                  />
                )
              )}
            </TransactionItemDrawer>
            <ConfirmModal />
          </>
        )}
      </>
    );
  }
);
InnerTable.displayName = 'InnerTable';

export const FallbackTable: FC<
  TransactionItemsViewTableProps & {
    emptyType: 'loading' | 'error';
  }
> = memo(({ tableBodyHeight, emptyType }) => {
  const repeatCount = useMemo(
    () =>
      Math.floor(
        (tableBodyHeight - 24) / (SKELETON_HEIGHT_XS + SKELETON_GAP_XS)
      ),
    [tableBodyHeight]
  );

  const tbodyStyle = useMemo(() => {
    return {
      height: tableBodyHeight,
    };
  }, [tableBodyHeight]);

  const tableProps: Omit<TransactionItemsTableProps, 'scroll'> = useMemo(() => {
    return {
      data: [],
      emptyText:
        emptyType === 'loading' ? (
          <LoadingSkeletonList repeatCount={repeatCount} />
        ) : (
          <></>
        ),
      emptyType,
      legacy: false,
      hideFooterPagination: true,
      hideHeaderPagination: true,
      tableAction: <TableAction isLoading={emptyType === 'loading'} />,
      components:
        emptyType === 'error'
          ? {
              body: {
                wrapper: ({ children }: { children: React.ReactNode }) => (
                  <tbody style={tbodyStyle}>{children}</tbody>
                ),
              },
            }
          : undefined,
    };
  }, [emptyType, repeatCount, tbodyStyle]);

  return <TransactionItemsTable table={tableProps} onDetailClick={() => {}} />;
});
FallbackTable.displayName = 'FallbackTable';

export const TransactionItemsTableView: FC<TransactionItemsViewTableProps> =
  memo((props) => {
    return (
      <ErrorBoundary fallback={<FallbackTable {...props} emptyType='error' />}>
        <Suspense fallback={<FallbackTable {...props} emptyType='loading' />}>
          <InnerTable {...props} />
        </Suspense>
      </ErrorBoundary>
    );
  });
TransactionItemsTableView.displayName = 'TransactionItemsTableView';
